/**
 * Promotion tiers — single source of truth shared by client + reused (as a
 * mirror) in functions/listingPromotionFn.js and functions/promotionCartFn.js.
 *
 * Keep the structure in sync with the server-side PROMOTION_TIERS map.
 */
export const PROMOTION_TIERS = Object.freeze({
  boost:     { key: "boost",     label: "Boost",     tokenCost: 10,  durationDays: 1,  priority: 1, blurb: "1-day push to the top of relevant searches." },
  spotlight: { key: "spotlight", label: "Spotlight", tokenCost: 30,  durationDays: 3,  priority: 2, blurb: "3-day spotlight placement." },
  featured:  { key: "featured",  label: "Featured",  tokenCost: 50,  durationDays: 7,  priority: 3, blurb: "1-week featured card placement." },
  premium:   { key: "premium",   label: "Premium",   tokenCost: 150, durationDays: 30, priority: 4, blurb: "30-day premium placement." },
});

/**
 * Long-form copy for the tier info modal shown on the Promotions tab.
 * Each entry describes how the tier changes placement, ranking, and how
 * stacks combine. `bullets` render as a short checklist in the modal body.
 */
export const PROMOTION_TIER_INFO = Object.freeze({
  boost: {
    label: "Boost",
    description:
      "A short, low-cost push that lifts your listing to the top of relevant searches and category pages for one day. Great for testing the waters or capitalizing on a sudden burst of demand.",
    bullets: [
      "Floats your listing above non-promoted results for 24 hours",
      "Ranks below Spotlight, Featured, and Premium stacks",
      "Stack multiple Boosts to extend the run day-by-day",
      "Cost: 10 Doubloons per day",
    ],
    bestFor: "Quick visibility bumps and weekend specials.",
  },
  spotlight: {
    label: "Spotlight",
    description:
      "A 3-day spotlight slot that places your listing in the highlighted spotlight row on category and search pages with a distinct visual treatment.",
    bullets: [
      "Featured in the spotlight strip for 3 consecutive days",
      "Outranks Boost; ranks below Featured and Premium",
      "Stack multiple Spotlights for back-to-back runs",
      "Cost: 30 Doubloons (10/day, 33% cheaper than back-to-back Boosts)",
    ],
    bestFor: "Mid-week promos and short campaigns.",
  },
  featured: {
    label: "Featured",
    description:
      "A full week in the Featured card row across the homepage, category, and search results. Substantially more impressions than Boost or Spotlight, and a more prominent visual treatment.",
    bullets: [
      "7 consecutive days in the Featured carousel",
      "Outranks Boost and Spotlight; ranks below Premium",
      "Bigger card art and a Featured badge",
      "Cost: 50 Doubloons (≈7/day — best value per day among non-Premium tiers)",
    ],
    bestFor: "New listings, grand openings, and weekly specials.",
  },
  premium: {
    label: "Premium",
    description:
      "Top-tier placement for 30 days. Your listing appears in the Premium hero rotation on the homepage and is given top priority in search and category rankings across the site.",
    bullets: [
      "30 consecutive days in the Premium hero rotation",
      "Highest placement priority across search, map, and category pages",
      "Premium badge and largest card treatment",
      "Cost: 150 Doubloons (5/day — the lowest daily rate)",
    ],
    bestFor: "Established listings looking for sustained reach over a month.",
  },
  custom: {
    label: "Custom dates",
    description:
      "Choose an exact start and end date/time in your timezone instead of a canonical 1/3/7/30-day stack. Useful for tying promotion to specific events, holidays, or business hours.",
    bullets: [
      "Pick any start + end window (minimum 1 day)",
      "Priced from the Boost daily rate (10 Doubloons/day)",
      "Ranks like Boost — for higher placement, use Spotlight/Featured/Premium",
      "Cancellable any time before the scheduled start",
    ],
    bestFor: "Holiday weekends, festival days, and time-boxed campaigns.",
  },
});

export const PROMOTION_TYPE_KEYS = Object.freeze(["boost", "spotlight", "featured", "premium"]);
export const PROMOTION_TYPE_KEYS_WITH_CUSTOM = Object.freeze([...PROMOTION_TYPE_KEYS, "custom"]);

export const PROMOTION_STACK_COUNT_MAX = 30;

/** Compute cost for a canonical stack (tier × count). */
export function canonicalStackCost(typeKey, count = 1) {
  const tier = PROMOTION_TIERS[typeKey];
  if (!tier) return 0;
  const n = Math.max(1, Math.min(PROMOTION_STACK_COUNT_MAX, Math.floor(Number(count) || 1)));
  return tier.tokenCost * n;
}

/** Compute cost for a custom-duration stack of `baseTypeKey` over `actualDays`. */
export function customStackCost(baseTypeKey, actualDays) {
  const tier = PROMOTION_TIERS[baseTypeKey];
  if (!tier || !(actualDays > 0)) return 0;
  const perDay = tier.tokenCost / tier.durationDays;
  return Math.ceil(perDay * actualDays);
}

/** Effective canonical duration: tier days × count. */
export function canonicalStackDurationDays(typeKey, count = 1) {
  const tier = PROMOTION_TIERS[typeKey];
  if (!tier) return 0;
  const n = Math.max(1, Math.min(PROMOTION_STACK_COUNT_MAX, Math.floor(Number(count) || 1)));
  return tier.durationDays * n;
}
